import { FC, useCallback, useEffect, useId, useState } from 'react';

import { RiLogoutCircleLine } from 'react-icons/ri';
import { IoIosArrowDown } from 'react-icons/io';

import Image from 'next/image';

import { useAuth } from '~/common/providers/auth';
import { Typography } from '~/common/components/Typography';
import { GenericMenuItem } from '~/common/components/GenericMenuItem';

import {
  InlineLogoutButton,
  InlineLogoutButtonWrapper,
  StyAvatar,
  SoftPopoverWrapper,
  StyMenuContainer,
  StyPopoverContainer,
  StyUserContent,
} from './styles';

interface IUserMenuProps {
  displayUser?: {
    name?: string;
    avatar?: string;
    profile?: {
      name?: string;
    };
  } | null;
  onLogout?: () => void;
  hideProfileItem?: boolean;
  showInlineLogoutButton?: boolean;
  inverted?: boolean;
}

export const UserMenu: FC<IUserMenuProps> = ({
  displayUser,
  onLogout,
  hideProfileItem = false,
  showInlineLogoutButton = false,
  inverted = false,
}) => {
  const { user, logout } = useAuth();
  const activeUser = displayUser || user;
  const handleLogout = onLogout || logout;
  const popupId = useId();
  const [open, setOpen] = useState(false);

  const handleToggleArrow = (): void => {
    setOpen((prev) => !prev);
  };

  function formatName(name?: string): string {
    if (!name) {
      return '';
    }

    const splitName = name.trim().split(/\s+/);

    if (splitName.length <= 3) {
      return name;
    }

    const firstname = splitName[0];
    const lastName = splitName[splitName.length - 1];

    return `${firstname} ${lastName}`;
  }

  function getUserInitials(name?: string): string {
    if (!name) {
      return '??';
    }

    const splitName = name.trim().split(/\s+/).filter(Boolean);

    if (!splitName.length) {
      return '??';
    }

    const firstInitial = splitName[0][0] || '';
    const lastInitial =
      splitName.length > 1
        ? splitName[splitName.length - 1][0] || ''
        : splitName[0][1] || '';

    return `${firstInitial}${lastInitial}`.toUpperCase();
  }

  const handleClickOutside = useCallback(
    (event: Event): void => {
      const element = document.getElementById(popupId);

      if (element && !element.contains(event.target as HTMLElement)) {
        setOpen(false);
      }
    },
    [popupId]
  );

  useEffect(() => {
    if (showInlineLogoutButton) {
      return;
    }

    if (open) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside, open, showInlineLogoutButton]);

  return (
    <StyPopoverContainer id={popupId}>
      <StyUserContent
        onClick={showInlineLogoutButton ? undefined : handleToggleArrow}
        isOpen={showInlineLogoutButton ? false : open}
        isClickable={!showInlineLogoutButton}
        $inverted={inverted}
      >
        <Typography.Label
          size="medium"
          color="neutral.400"
          style={inverted ? { color: '#FFFFFF' } : undefined}
        >
          {formatName(activeUser?.name)}
        </Typography.Label>

        <StyAvatar>
          {activeUser?.avatar ? (
            <Image
              src={activeUser.avatar}
              alt={activeUser?.name || 'Avatar do usuario'}
              width={32}
              height={32}
            />
          ) : (
            <span>{getUserInitials(activeUser?.name)}</span>
          )}
        </StyAvatar>

        {!showInlineLogoutButton && <IoIosArrowDown />}
      </StyUserContent>

      {showInlineLogoutButton ? (
        <InlineLogoutButtonWrapper>
          <InlineLogoutButton type="button" title="Sair" onClick={handleLogout}>
            <RiLogoutCircleLine size={24} />
          </InlineLogoutButton>
        </InlineLogoutButtonWrapper>
      ) : (
        <SoftPopoverWrapper visible={open}>
          <StyMenuContainer
            {...(!open && {
              style: {
                pointerEvents: 'none',
              },
            })}
          >
            {!hideProfileItem && (
              <>
                <GenericMenuItem
                  selected={false}
                  title={`Perfil: ${
                    activeUser?.profile?.name || 'Administrador'
                  }`}
                  index={1}
                  withoutClick
                  allowTitleWrap
                />
                <hr />
              </>
            )}
            <GenericMenuItem
              selected={false}
              icon={RiLogoutCircleLine}
              title="Sair"
              index={hideProfileItem ? 1 : 2}
              onClick={handleLogout}
              iconColor="red"
              withoutClick={!open}
            />
          </StyMenuContainer>
        </SoftPopoverWrapper>
      )}
    </StyPopoverContainer>
  );
};
